import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Trash2, ArchiveRestore, RotateCcw, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatApiError } from "../lib/format";

const COLLECTION_LABEL = {
  clients: "Klien",
  bookings: "Pemesanan",
  inquiries: "Inquiry",
  quotations: "Penawaran",
  invoices: "Invoice",
  leads: "Lead",
  vendor_payables: "Vendor Payable",
  job_wages: "Fee Crew",
  reports: "Laporan",
  users: "Karyawan",
};

const summary = (snap, coll) => {
  if (!snap) return "-";
  if (coll === "clients") return `${snap.bride_name || "-"} & ${snap.groom_name || "-"}`;
  if (coll === "users") return `${snap.name || snap.email || "-"}`;
  if (coll === "bookings") return `${snap.booking_code || ""} — ${snap.event_name || "-"}`;
  if (coll === "quotations") return `${snap.quotation_code || snap.root_code || ""} v${snap.version || 1}`;
  if (coll === "invoices") return `${snap.invoice_code || ""}`;
  if (coll === "inquiries") return `${snap.inquiry_code || ""} — ${snap.bride_name || snap.name || "-"}`;
  if (coll === "leads") return `${snap.name || "-"} • ${snap.event_type || "-"}`;
  if (coll === "job_wages") return `${snap.staff_name || "-"} — ${snap.role || "-"}`;
  if (coll === "vendor_payables") return `${snap.vendor_name || ""} — ${snap.description || ""}`;
  return snap.id || "-";
};

export default function RecycleBin() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeOneTarget, setPurgeOneTarget] = useState(null);

  const load = async () => {
    try {
      const params = tab === "all" ? {} : { collection: tab };
      const { data } = await api.get("/deletion-audit", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return summary(r.snapshot, r.collection).toLowerCase().includes(s) ||
      (r.deleted_by_email || "").toLowerCase().includes(s) ||
      (r.collection || "").toLowerCase().includes(s);
  }), [rows, q]);

  const doRestore = async (r) => {
    setBusy(true);
    try {
      await api.post(`/deletion-audit/${r.id}/restore`);
      toast.success(`${COLLECTION_LABEL[r.collection] || r.collection} berhasil direstore`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doPurgeExpired = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deletion-audit/purge-expired");
      toast.success(`${data.purged} record dihapus permanen`);
      setPurgeConfirm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doPurgeOne = async () => {
    if (!purgeOneTarget) return;
    setBusy(true);
    try {
      await api.delete(`/deletion-audit/${purgeOneTarget.id}`);
      toast.success("Record dihapus permanen");
      setPurgeOneTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const daysAgo = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  };

  return (
    <div className="space-y-6" data-testid="recycle-bin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Recycle Bin</h1>
          <p className="text-sm text-[#5C6066] mt-1">Data yang dihapus tersimpan 30 hari sebelum dihapus permanen. Owner/GM dapat merestore atau menghapus permanen kapan saja.</p>
        </div>
        <Button variant="outline" onClick={() => setPurgeConfirm(true)} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="btn-purge-expired">
          <Trash2 className="w-4 h-4 mr-1.5" /> Purge yang > 30 hari
        </Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab} data-testid="recycle-tabs">
          <TabsList>
            <TabsTrigger value="all">Semua</TabsTrigger>
            <TabsTrigger value="clients">Klien</TabsTrigger>
            <TabsTrigger value="bookings">Pemesanan</TabsTrigger>
            <TabsTrigger value="quotations">Penawaran</TabsTrigger>
            <TabsTrigger value="invoices">Invoice</TabsTrigger>
            <TabsTrigger value="users">Karyawan</TabsTrigger>
            <TabsTrigger value="inquiries">Inquiry</TabsTrigger>
            <TabsTrigger value="leads">Lead</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari nama, email, kode..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="recycle-search" />
        </div>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Kategori</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Dihapus oleh</th>
                <th className="text-left px-4 py-3">Alasan</th>
                <th className="text-left px-4 py-3">Tanggal</th>
                <th className="text-right px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Recycle bin kosong.</td></tr>
              )}
              {filtered.map((r) => {
                const days = daysAgo(r.deleted_at);
                const nearExpire = days >= 25;
                return (
                  <tr key={r.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`audit-row-${r.id}`}>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className="text-[10px] uppercase">{COLLECTION_LABEL[r.collection] || r.collection}</Badge>
                    </td>
                    <td className="px-4 py-3 font-medium">{summary(r.snapshot, r.collection)}</td>
                    <td className="px-4 py-3">
                      <div className="text-[13px]">{r.deleted_by_name || "-"}</div>
                      <div className="text-[11px] text-[#8C9096]">{r.deleted_by_email}</div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#5C6066] max-w-xs truncate" title={r.reason}>{r.reason || "—"}</td>
                    <td className="px-4 py-3 text-[11px] text-[#8C9096]">
                      {formatDate(r.deleted_at)}
                      <div className={`text-[10px] ${nearExpire ? "text-rose-700 font-medium" : ""}`}>
                        {days} hari lalu{nearExpire && " • hampir kadaluarsa"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => doRestore(r)} disabled={busy} data-testid={`restore-${r.id}`}>
                          <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setPurgeOneTarget(r)} disabled={busy}
                          className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`purge-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={purgeConfirm} onOpenChange={setPurgeConfirm}>
        <DialogContent data-testid="purge-expired-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/> Purge Data Kadaluarsa</DialogTitle>
            <DialogDescription>Ini akan menghapus PERMANEN semua data yang dihapus lebih dari 30 hari lalu. Data yang di-purge tidak dapat direstore lagi.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeConfirm(false)}>Batal</Button>
            <Button variant="destructive" onClick={doPurgeExpired} disabled={busy} data-testid="confirm-purge-expired">
              <Trash2 className="w-4 h-4 mr-1.5" /> Purge Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!purgeOneTarget} onOpenChange={(v) => !v && setPurgeOneTarget(null)}>
        <DialogContent data-testid="purge-one-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/> Hapus Permanen</DialogTitle>
            <DialogDescription>Record ini akan dihapus permanen dari Recycle Bin. Tidak dapat direstore lagi.</DialogDescription>
          </DialogHeader>
          {purgeOneTarget && (
            <div className="p-2 bg-rose-50 border border-rose-200 rounded text-sm font-mono">
              {COLLECTION_LABEL[purgeOneTarget.collection]}: {summary(purgeOneTarget.snapshot, purgeOneTarget.collection)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOneTarget(null)}>Batal</Button>
            <Button variant="destructive" onClick={doPurgeOne} disabled={busy} data-testid="confirm-purge-one">
              <Trash2 className="w-4 h-4 mr-1.5" /> Hapus Permanen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
